import React from 'react'
import PropTypes from 'prop-types'
import { Formik, Form } from 'formik'

import useHTTPRequest, { RequestStatus } from '@hooks/useHTTPRequest'
import { Modal, ModalHeader, ModalSubHeader, ModalContent } from '@components/Modal'
import { Button } from "@components/Button"
import { NativeSelectWithLabel } from "@components/Form/Fields"

import './create-investment-proposal.scss'

export const CreateInvestmentProposalModal = ({
  open,
  onClose,
  assetId,
  clientId,
  riskProfileId,
  dfmIntegration,
  assets
}) => {
  const request = useHTTPRequest({
    url: `/api/investment-proposals`,
    method: 'POST'
  })
  const closeModalAndResetRequest = () => {
    request.reset({ resetState: false })
    onClose()
  }
  const handleSubmit = async (values) => {
    const payload = {
      client_id: clientId,
      risk_profile_id: riskProfileId,
      asset_id: assetId || values.assetId,
      dfm_integration_id: dfmIntegration.id,
    }
    const data = await request.execute(payload)
    const redirectURL = `${dfmIntegration.externalURL}&wbid=${data.investmentProposalId}`

    // In lieu of a success animation after creating the investment proposal,
    // this artificial delay ensures the page does not redirect _too quickly_
    // after clicking the button
    setTimeout(() => {
      window.location = redirectURL
    }, 1000)
  }
  const assetSelectOptions = assets.map(asset => ({
    name: asset.name,
    value: asset.id
  }))
  let ContentComponent
  switch (request.status) {
    case RequestStatus.error:
      ContentComponent = ErrorContent
      break
    default:
      ContentComponent = FormContent
  }

  return (
    <Modal open={open} onClose={closeModalAndResetRequest}>
      <ContentComponent
        onClose={closeModalAndResetRequest}
        busy={request.loading || request.success}
        handleSubmit={handleSubmit}
        assetSelectOptions={assetSelectOptions}
        assetId={assetId} />
    </Modal>
  )
}

CreateInvestmentProposalModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tenantSlug: PropTypes.string.isRequired,
  clientId: PropTypes.number.isRequired,
  riskProfileId: PropTypes.number.isRequired,
  assetId: PropTypes.number,
  dfmIntegration: PropTypes.shape({
    id: PropTypes.number.isRequired,
    externalURL: PropTypes.string.isRequired
  })
}

const FormContent = ({ onClose, busy, handleSubmit, assetSelectOptions, assetId }) => {
  const validAssetIdSelected = assetId || assetSelectOptions.length > 0

  return <>
    <ModalHeader>Create an investment proposal</ModalHeader>
    <ModalSubHeader>
      Continue in Strategiq Capital’s platform
    </ModalSubHeader>
    <ModalContent>
      <Formik
        onSubmit={handleSubmit}
        initialValues={{ assetId: assetSelectOptions[0]?.value }}>
        {({ isValid }) =>
          <Form className='app-form create-investment-proposal__form'>
            {(!assetId && assetSelectOptions.length > 0) &&
              <NativeSelectWithLabel
                className="create-investment-proposal__form__select"
                name="assetId"
                label="Select asset for proposal"
                options={assetSelectOptions} />
            }
            {(!assetId && assetSelectOptions.length === 0) &&
              <p className="create-investment-proposal__form__instructions">
                No assets match the criteria for an investment proposal. An asset must be linked with a service provider and contract number, and be a supported asset type to create a proposal.
              </p>
            }

            {assetSelectOptions.length !== 0 &&
              <p className="create-investment-proposal__form__instructions">
                You will be redirected to an external platform to complete the process.
                <br />
                The plan will be included in the proposal automatically.
              </p>
            }

            <div className='create-investment-proposal__form__actions'>
              <Button secondary disabled={busy} onClick={onClose}>Cancel</Button>
              {validAssetIdSelected &&
                <Button primary disabled={!isValid || busy} type='submit'>
                  {busy ? 'Loading...' : 'Start proposal'}
                </Button>
              }
            </div>
          </Form>
        }
      </Formik>
    </ModalContent>
  </>
}

const ErrorContent = ({ onClose }) => <>
  <ModalHeader>Something went wrong</ModalHeader>
  <ModalSubHeader>An error occurred starting the proposal</ModalSubHeader>
  <ModalContent className='create-investment-proposal-modal__error-content'>
    <p>Try again in a few minutes, and rest assured we’ve been notified of the problem.</p>
    <Button primary onClick={onClose}>Close</Button>
  </ModalContent>
</>
